import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Calendar } from "lucide-react";

/**
 * Phase 8M.3 Item 5 — Date picker chips card.
 *
 * Rendered when the router agent detected a flight search without a
 * date. Tapping a chip rephrases the original intent into a concrete
 * follow-up message (e.g. "DEL → DXB on 2026-02-21") that re-enters
 * the chat flow with a fully-resolved date in scope.
 */

function pad(n) {
    return String(n).padStart(2, "0");
}

function toIso(d) {
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

/**
 * Resolve a chip's symbolic value ("today", "tomorrow", "this weekend",
 * "next week") into a concrete YYYY-MM-DD. This runs on the client so
 * the user's local timezone defines "today", and the LLM router gets
 * an unambiguous date string to pass into `search_flights`.
 */
function resolveChipToIso(phrase) {
    const now = new Date();
    if (phrase === "today") return toIso(now);
    if (phrase === "tomorrow") {
        const d = new Date(now);
        d.setDate(d.getDate() + 1);
        return toIso(d);
    }
    if (phrase === "this weekend") {
        // Upcoming Saturday. If today IS Saturday, pick today.
        const d = new Date(now);
        const dow = d.getDay(); // 0=Sun..6=Sat
        const delta = dow === 6 ? 0 : (6 - dow + 7) % 7;
        d.setDate(d.getDate() + delta);
        return toIso(d);
    }
    if (phrase === "next week") {
        const d = new Date(now);
        d.setDate(d.getDate() + 7);
        return toIso(d);
    }
    // Fallback — let backend best-effort it.
    return phrase;
}
export default function DatePickerChipsCard({ card, onPick }) {
    const { t } = useTranslation();
    const [pickerOpen, setPickerOpen] = useState(false);
    const [picked, setPicked] = useState("");

    const route = card.route_label || "";
    const origin = card.origin || "";
    const destination = card.destination || "";

    const sendForChip = (chip) => {
        if (chip.value === "__pick__") {
            setPickerOpen((v) => !v);
            return;
        }
        const iso = resolveChipToIso(chip.value);
        const msg = origin && destination
            ? `Cheapest flight from ${origin} to ${destination} on ${iso}`
            : `flights on ${iso}`;
        onPick(msg);
    };

    const sendPicked = () => {
        if (!picked) return;
        const msg = origin && destination
            ? `Cheapest flight from ${origin} to ${destination} on ${picked}`
            : `flights on ${picked}`;
        onPick(msg);
    };

    return (
        <article
            className="relative overflow-hidden rounded-2xl border border-[#E5E5E5] bg-white p-5"
            data-testid="date-picker-card"
        >
            <span
                aria-hidden
                className="absolute right-4 top-4 inline-block h-2.5 w-2.5 rounded-full bg-[#0A0A0A] ring-4 ring-[#0A0A0A]/10"
            />
            <div className="flex items-center gap-2">
                <span className="flex h-6 w-6 items-center justify-center rounded-full bg-[#F5F5F5]">
                    <Calendar size={13} strokeWidth={2} className="text-[#0A0A0A]" />
                </span>
                <span className="text-[13px] font-medium uppercase tracking-wider text-[#737373]">
                    {t("chat.ask_date_prompt")}
                </span>
            </div>
            {route && (
                <div
                    className="mt-3 text-[18px] font-semibold tracking-tight text-[#0A0A0A]"
                    data-testid="date-picker-route"
                >
                    {route}
                </div>
            )}
            <p className="mt-2 text-[13px] text-[#737373]">{t("chat.ask_date_subtitle")}</p>
            <div className="mt-4 flex flex-wrap gap-2">
                {(card.chips || []).map((chip) => (
                    <button
                        key={chip.id}
                        type="button"
                        onClick={() => sendForChip(chip)}
                        data-testid={`date-chip-${chip.id}`}
                        className="inline-flex items-center gap-1.5 rounded-full border border-[#E5E5E5] bg-white px-3 py-1.5 text-[12px] font-medium text-[#0A0A0A] transition hover:border-[#0A0A0A] hover:bg-[#0A0A0A] hover:text-white"
                    >
                        <span className="inline-block h-1.5 w-1.5 rounded-full bg-[#2563EB]" />
                        {t(chip.label_key)}
                    </button>
                ))}
            </div>
            {pickerOpen && (
                <div className="mt-4 flex items-center gap-2" data-testid="date-picker-input-wrap">
                    <input
                        type="date"
                        value={picked}
                        onChange={(e) => setPicked(e.target.value)}
                        min={new Date().toISOString().slice(0, 10)}
                        className="rounded-lg border border-[#E5E5E5] bg-white px-3 py-2 text-[13px] text-[#0A0A0A] focus:border-[#0A0A0A] focus:outline-none"
                        data-testid="date-picker-input"
                    />
                    <button
                        type="button"
                        onClick={sendPicked}
                        disabled={!picked}
                        data-testid="date-picker-confirm"
                        className="inline-flex items-center justify-center rounded-lg bg-[#0A0A0A] px-4 py-2 text-[12px] font-medium text-white transition hover:bg-[#262626] disabled:opacity-40"
                    >
                        Search flights
                    </button>
                </div>
            )}
        </article>
    );
}
